"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.ActionBlockRunSchema = exports.ACTION_RUN_SOURCES = exports.ActionStepResultSchema = exports.ACTION_BLOCKS_V1 = exports.ACTION_INTENTS = exports.ACTION_STEPS = void 0;
exports.actionBlockById = actionBlockById;
exports.orderedSteps = orderedSteps;
exports.blocksForEnforcement = blocksForEnforcement;
exports.enforcementForBlocks = enforcementForBlocks;
exports.runStatusOf = runStatusOf;
const zod_1 = require("zod");
/**
 * ACTION BLOCKS — the things we can DO to a tenant, as data.
 *
 * A block is a named list of steps. The same block is run by a super admin
 * pressing a button and by a financing policy reacting to a default, so a fix
 * to what "suspend features" means reaches both without either being edited.
 *
 * Adding a fifth step later edits this file, not any screen — the same
 * property that makes ORDER_STAGES_V1 extensible.
 */
exports.ACTION_STEPS = ['packages', 'pos_terminals', 'payment_types', 'storefront'];
exports.ACTION_INTENTS = ['grant', 'revoke', 'none'];
const ALL_STEPS = ['packages', 'pos_terminals', 'payment_types', 'storefront'];
exports.ACTION_BLOCKS_V1 = [
    {
        id: 'activate_packages', label: 'Activate packages', intent: 'grant',
        steps: ['packages'],
        description: 'Turn on exactly the packages this tenant is billed for.',
    },
    {
        id: 'activate_all', label: 'Activate all access', intent: 'grant',
        steps: ALL_STEPS,
        description: 'Restore everything recorded at suspension.',
    },
    {
        id: 'suspend_features', label: 'Suspend features', intent: 'revoke',
        steps: ['packages'],
        description: 'They keep the register; the extras go.',
    },
    {
        id: 'suspend_all', label: 'Suspend all access', intent: 'revoke',
        steps: ALL_STEPS,
        description: 'The register stops. Use for high-value hardware.',
    },
    {
        /**
         * ZERO STEPS ON PURPOSE. "Chase the money, touch nothing" is a real,
         * selectable outcome, and modelling it as an empty block keeps every
         * policy expressible as a list of blocks with no special cases.
         */
        id: 'collections_only', label: 'Collections only', intent: 'none',
        steps: [],
        description: 'Never blocks the store — chase the money separately.',
    },
];
function actionBlockById(id) {
    return exports.ACTION_BLOCKS_V1.find((b) => b.id === id);
}
/**
 * The order the steps actually run in.
 *
 * REVOKE runs in declared order: packages first, so feature access is gone
 * before the register is told to lock. GRANT runs REVERSED: the register comes
 * back first and packages last, so a tenant never briefly holds features on a
 * still-locked terminal.
 */
function orderedSteps(block) {
    return block.intent === 'grant' ? [...block.steps].reverse() : block.steps;
}
/**
 * The legacy single-value `enforcement` enum, as a list of blocks.
 *
 * Used when a SKU has no `enforcementActions` yet, so every policy stored
 * before this feature existed keeps its exact present meaning.
 *
 * An UNRECOGNISED value maps to `collections_only` — the outcome that touches
 * nothing. Guessing "suspend" from a value we do not understand would cut off
 * a paying merchant over a parsing failure.
 */
function blocksForEnforcement(enforcement) {
    switch (enforcement) {
        case 'suspend_all': return ['suspend_all'];
        case 'suspend_feature': return ['suspend_features', 'collections_only'];
        case 'collections_only': return ['collections_only'];
        default: return ['collections_only'];
    }
}
/**
 * The closest legacy value for a list of blocks, written ALONGSIDE the list so
 * readers that only know `enforcement` never meet a value they cannot parse.
 *
 * Hardest-wins, and an empty list is `collections_only` — never an accidental
 * suspension.
 */
function enforcementForBlocks(blockIds) {
    if (blockIds.includes('suspend_all'))
        return 'suspend_all';
    if (blockIds.includes('suspend_features'))
        return 'suspend_feature';
    return 'collections_only';
}
exports.ActionStepResultSchema = zod_1.z.object({
    step: zod_1.z.enum(exports.ACTION_STEPS),
    ok: zod_1.z.boolean(),
    detail: zod_1.z.string().optional(),
    error: zod_1.z.string().optional(),
    /** Set when the step succeeded but did less than it was asked to. See the shrinkage guard. */
    degraded: zod_1.z.string().optional(),
});
exports.ACTION_RUN_SOURCES = ['manual', 'policy', 'onboarding'];
exports.ActionBlockRunSchema = zod_1.z.object({
    runId: zod_1.z.string().min(1),
    blockId: zod_1.z.string().min(1),
    intent: zod_1.z.enum(exports.ACTION_INTENTS),
    source: zod_1.z.enum(exports.ACTION_RUN_SOURCES),
    actor: zod_1.z.string().min(1),
    reason: zod_1.z.string().optional(),
    status: zod_1.z.enum(['ok', 'partial', 'failed']),
    steps: zod_1.z.array(exports.ActionStepResultSchema),
    orderId: zod_1.z.string().optional(),
    at: zod_1.z.number(),
});
/**
 * The run's overall verdict. `partial` exists because for a REVOKE block,
 * stopping at the first failure leaves a half-suspended tenant that is worse
 * than either end state — so every step is attempted and the mixed result is
 * reported honestly rather than rounded to success or failure.
 */
function runStatusOf(steps) {
    if (steps.length === 0)
        return 'ok';
    const okCount = steps.filter((s) => s.ok).length;
    if (okCount === steps.length)
        return 'ok';
    if (okCount === 0)
        return 'failed';
    return 'partial';
}
//# sourceMappingURL=actionBlocks.js.map